import { useState } from 'react';

const Tech = () => {
  const [hoveredTech, setHoveredTech] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const technologies = [
    {
      category: "Frontend Development",
      color: "#61dafb",
      icon: "💻",
      techs: [
        { name: "React.js", icon: "⚛️", description: "UI Library" },
        { name: "Next.js", icon: "▲", description: "React Framework" },
        { name: "Vue.js", icon: "💚", description: "Progressive Framework" },
        { name: "Angular", icon: "🅰️", description: "Enterprise Framework" },
        { name: "TypeScript", icon: "📘" },
        { name: "Tailwind CSS", icon: "🎨" },
        { name: "HTML5", icon: "🌐" },
        { name: "CSS3", icon: "🎨" }
      ]
    },
    {
      category: "Mobile Development",
      color: "#3ddc84",
      techs: [
        { name: "React Native", icon: "⚛️" },
        { name: "Flutter", icon: "🦋" },
        { name: "Android Native", icon: "🤖" },
        { name: "iOS (Swift)", icon: "🍎" },
        { name: "Kotlin", icon: "🔷" },
        { name: "Java", icon: "☕" }
      ]
    },
    {
      category: "Backend",
      color: "#68a063",
      techs: [
        { name: "Node.js", icon: "🟢" },
        { name: "Express.js", icon: "🚂" },
        { name: "Python", icon: "🐍" },
        { name: "Django", icon: "🎸" },
        { name: "Java Spring", icon: "☕" },
        { name: "PHP Laravel", icon: "🔴" },
        { name: ".NET Core", icon: "💠" },
        { name: "Go", icon: "🔷" }
      ]
    },
    {
      category: "Database",
      color: "#4db33d",
      techs: [
        { name: "MongoDB", icon: "🍃" },
        { name: "PostgreSQL", icon: "🐘" },
        { name: "MySQL", icon: "🐬" },
        { name: "Redis", icon: "📦" },
        { name: "Firebase", icon: "🔥" },
        { name: "Cassandra", icon: "💾" }
      ]
    },
    {
      category: "Cloud & DevOps",
      color: "#ff9900",
      techs: [
        { name: "AWS", icon: "☁️" },
        { name: "Google Cloud", icon: "☁️" },
        { name: "Azure", icon: "☁️" },
        { name: "Firebase", icon: "🔥" },
        { name: "Docker", icon: "🐳" },
        { name: "Kubernetes", icon: "☸️" },
        { name: "Jenkins", icon: "🔧" },
        { name: "GitLab CI/CD", icon: "🦊" }
      ]
    },
    {
      category: "Enterprise & Security",
      color: "#3ddc84",
      techs: [
        { name: "Android EMM", icon: "🔐" },
        { name: "MDM Solutions", icon: "📲" },
        { name: "DPC Systems", icon: "🛡️" },
        { name: "Zero Trust Security", icon: "🔒" },
        { name: "OAuth & SSO", icon: "🔑" },
        { name: "Encryption", icon: "🔒" }
      ]
    },
    {
      category: "AI & ML",
      color: "#8e44ad",
      techs: [
        { name: "TensorFlow", icon: "🧠" },
        { name: "PyTorch", icon: "🔥" },
        { name: "OpenAI", icon: "🤖" },
        { name: "Hugging Face", icon: "🤗" }
      ]
    },
    {
      category: "Blockchain",
      color: "#f7931a",
      techs: [
        { name: "Ethereum", icon: "💎" },
        { name: "Solidity", icon: "📜" },
        { name: "Web3.js", icon: "🌐" },
        { name: "Smart Contracts", icon: "📝" }
      ]
    },
    {
      category: "Testing & QA",
      color: "#ff6b6b",
      techs: [
        { name: "Jest", icon: "🃏" },
        { name: "Cypress", icon: "🌲" },
        { name: "Selenium", icon: "🔍" },
        { name: "JUnit", icon: "✅" }
      ]
    }
  ];

  const displayedTechnologies = showAll ? technologies : technologies.slice(0, 4);

  // JSON-LD structured data — describes the tech stack as an ItemList,
  // helping search engines associate Tracklio Hub with these specific technologies.
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Tracklio Hub Technology Stack",
    "description": "Technologies and frameworks used by Tracklio Hub to build web, mobile, and enterprise software solutions.",
    "itemListElement": technologies.map((category, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": category.category,
      "item": {
        "@type": "DefinedTermSet",
        "name": category.category,
        "hasDefinedTerm": category.techs.map((tech) => ({
          "@type": "DefinedTerm",
          "name": tech.name
        }))
      }
    }))
  };

  const styles = {
    section: {
      padding: '100px 40px',
      background: 'linear-gradient(180deg, #f9fafb 0%, #ffffff 100%)',
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
    },
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 1,
    },
    header: {
      textAlign: 'center',
      marginBottom: '70px',
    },
    title: {
      fontSize: 'clamp(40px, 5vw, 56px)',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      marginBottom: '20px',
      lineHeight: '1.2',
    },
    subtitle: {
      fontSize: '18px',
      color: '#6b7280',
      maxWidth: '700px',
      margin: '0 auto',
      lineHeight: '1.8',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '32px',
      marginTop: '60px',
    },
    categoryCard: {
      backgroundColor: '#ffffff',
      borderRadius: '20px',
      padding: '40px 32px',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      border: '1px solid #e5e7eb',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      height: 'auto',
    },
    categoryCardHovered: {
      transform: 'translateY(-8px)',
      boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
      borderColor: 'transparent',
    },
    categoryTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '28px',
      paddingBottom: '16px',
      borderBottom: '4px solid',
      display: 'block',
      position: 'relative',
    },
    techGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
    },
    techItem: {
      backgroundColor: '#ffffff',
      padding: '14px 12px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      border: '2px solid #f3f4f6',
    },
    techItemHovered: {
      transform: 'scale(1.05)',
      boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
      borderColor: '#e5e7eb',
    },
    techIcon: {
      fontSize: '32px',
      flexShrink: 0,
    },
    techName: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      lineHeight: '1.2',
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '50px',
    },
    showMoreButton: {
      backgroundColor: '#667eea',
      color: '#ffffff',
      padding: '16px 48px',
      borderRadius: '50px',
      fontSize: '16px',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
    },
    showMoreButtonHover: {
      backgroundColor: '#764ba2',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
    },
  };

  return (
    <section style={styles.section} id="tech" aria-label="Our Technology Stack">
      {/* Structured data for search engines */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>Technology Stack We Use</h2>
          <p style={styles.subtitle}>
            We leverage cutting-edge technologies across frontend, backend, mobile, cloud,
            AI/ML, and enterprise security to build robust, scalable, and secure software
            solutions tailored to your business needs.
          </p>
        </div>

        <div style={styles.grid} role="list">
          {displayedTechnologies.map((category) => {
            const catKey = category.category;
            return (
              <section
                key={catKey}
                aria-labelledby={`tech-category-${catKey}`}
                role="listitem"
                style={{
                  ...styles.categoryCard,
                  border: `2px solid ${hoveredTech === `cat-${catKey}` ? category.color : '#f3f4f6'}`,
                  ...(hoveredTech === `cat-${catKey}` ? styles.categoryCardHovered : {}),
                }}
                onMouseEnter={() => setHoveredTech(`cat-${catKey}`)}
                onMouseLeave={() => setHoveredTech(null)}
              >
                <h3
                  id={`tech-category-${catKey}`}
                  style={{
                    ...styles.categoryTitle,
                    borderBottomColor: category.color,
                    color: hoveredTech === `cat-${catKey}` ? category.color : '#1f2937',
                  }}
                >
                  {category.category}
                </h3>

                <ul
                  style={{ ...styles.techGrid, listStyle: 'none', padding: 0, margin: 0 }}
                  aria-label={`${category.category} technologies`}
                >
                  {category.techs.map((tech) => {
                    const techKey = `${catKey}-${tech.name}`;
                    return (
                      <li
                        key={techKey}
                        style={{
                          ...styles.techItem,
                          ...(hoveredTech === techKey ? {
                            ...styles.techItemHovered,
                            borderColor: category.color,
                            backgroundColor: `${category.color}10`,
                          } : {}),
                        }}
                        onMouseEnter={() => setHoveredTech(techKey)}
                        onMouseLeave={() => setHoveredTech(null)}
                        title={tech.description ? `${tech.name} — ${tech.description}` : tech.name}
                      >
                        <span style={styles.techIcon} role="img" aria-label={`${tech.name} icon`}>
                          {tech.icon}
                        </span>
                        <span style={styles.techName}>{tech.name}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>

        {technologies.length > 4 && (
          <div style={styles.buttonContainer}>
            <button
              type="button"
              style={styles.showMoreButton}
              onClick={() => setShowAll(!showAll)}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#764ba2';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#667eea';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
              }}
              aria-expanded={showAll}
              aria-controls="tech"
              aria-label={showAll ? 'Show fewer technology categories' : 'Show all technology categories'}
            >
              {showAll ? 'Show Less' : 'Show More'}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Tech;